import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// Define the IColorShades interface
export interface IColorShades {
  10: string;
  50: string;
  100: string;
  200: string;
  300: string;
  400: string;
  500: string;
  600: string;
  700: string;
  800: string;
  900: string;
}

// Define the IColorTokens interface
export interface IColorTokens {
  primary: IColorShades;
  secondary: IColorShades;
  neutral: IColorShades;
  grey: IColorShades;
}

// Define the IThemeState interface with the cities array
export interface IThemeState {
  id: number;
  primary_colors: IColorShades;
  secondary_colors: IColorShades;
  neutral_colors: IColorShades;
  grey_colors: IColorShades;
  logo: string; // URL string (can be relative path)
  mode: "light" | "dark";
  logo_dark: string;
  logo_light: string;
  created_at: string; // ISO 8601 datetime string
  updated_at: string; // ISO 8601 datetime string
  company: number; // company ID
  cities: string[]; // Added cities array
  userModePreferences: Record<string, "light" | "dark">;
}

// Define the citiesInZimbabwe array
export const citiesInZimbabwe = [
  "Harare",
  "Bulawayo",
  "Mutare",
  "Gweru",
  "Chitungwiza",
  "Masvingo",
  "Kwekwe",
  "Kadoma",
  "Chinhoyi",
  "Marondera",
  "Bindura",
  "Hwange",
  "Shurugwi",
  "Norton",
  "Victoria Falls",
  "Zvishavane",
  "Ruwa",
  "Kariba",
  "Beitbridge",
  "Chipinge",
];

const initialColorShades: IColorShades = {
  10: "#C29363",
  50: "#C29363",
  100: "#111827",
  200: "#D1A07740",
  300: "#94A3B8",
  400: "#724E41",
  500: "#D1A077",
  600: "#E5BFA5",
  700: "#000000",
  800: "#f0f4f8",
  900: "#ffffff",
};

const initialState: IThemeState = {
  id: 1,
  primary_colors: { ...initialColorShades },
  secondary_colors: { ...initialColorShades },
  neutral_colors: { ...initialColorShades },
  grey_colors: { ...initialColorShades },
  logo: "",
  mode: "light",
  logo_dark: "",
  logo_light: "",
  created_at: "null",
  updated_at: "null",
  company: 1,
  cities: citiesInZimbabwe,
  userModePreferences: {},
};

const themeSlice = createSlice({
  name: "theme",
  initialState,
  reducers: {
    setTheme(state, action: PayloadAction<IThemeState>) {
      return { ...state, ...action.payload, mode: state.mode, userModePreferences: state.userModePreferences || {} };
    },
    clearTheme(state) {
      return { ...initialState, userModePreferences: state.userModePreferences || {} };
    },
    toggleMode(state, action: PayloadAction<string | undefined>) {
      const newMode = state.mode === "light" ? "dark" : "light";
      state.mode = newMode;
      if (!state.userModePreferences) {
        state.userModePreferences = {};
      }
      if (action.payload) {
        state.userModePreferences[action.payload] = newMode;
      }
    },
    loadUserMode(state, action: PayloadAction<string>) {
      if (!state.userModePreferences) {
        state.userModePreferences = {};
        return;
      }
      const savedMode = state.userModePreferences[action.payload];
      if (savedMode) {
        state.mode = savedMode;
      }
    },
  },
});

export const { setTheme, clearTheme, toggleMode, loadUserMode } = themeSlice.actions;
export default themeSlice.reducer;